import { Bot, ListTree, Sparkles } from "lucide-react";
import { FC } from "react";
import { FormattedMessage } from "react-intl";

export type PipelineMode = "USE_TOC" | "USE_SNOWFLAKE" | "USE_SMART_AGENT";

interface PipelineModeSelectorProps {
  selectedMode: PipelineMode;
  onModeChange: (mode: PipelineMode) => void;
  disabled?: boolean;
}

export const PipelineModeSelector: FC<PipelineModeSelectorProps> = ({
  selectedMode,
  onModeChange,
  disabled = false,
}) => {
  const handleModeSelect = (mode: PipelineMode) => {
    if (!disabled) {
      onModeChange(mode);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <div className={`grid gap-2 ${disabled ? "opacity-50" : ""}`}>
        <div
          onClick={() => handleModeSelect("USE_TOC")}
          className={`
            relative flex cursor-pointer rounded-lg border p-3 shadow-sm focus:outline-none
            ${selectedMode === "USE_TOC" ? "border-sky-600 ring-1 ring-sky-600 bg-sky-50" : "border-slate-300 hover:bg-slate-50"}
            ${disabled ? "cursor-not-allowed" : ""}
          `}
          role="button"
          tabIndex={disabled ? -1 : 0}
          onKeyDown={(e) => {
            if ((e.key === "Enter" || e.key === " ") && !disabled) {
              handleModeSelect("USE_TOC");
            }
          }}
        >
          <div className="flex w-full items-center justify-between">
            <div className="flex items-center">
              <div className="text-sm">
                <div className="flex items-center gap-2 font-medium text-slate-900">
                  <ListTree className="h-4 w-4 text-slate-500" />
                  <FormattedMessage
                    id="casesList.createCaseDialog.pipelineMode.useToc.title"
                    defaultMessage="Use Table of Contents"
                  />
                </div>
                <p className="text-slate-500 mt-1 text-xs">
                  <FormattedMessage
                    id="casesList.createCaseDialog.pipelineMode.useToc.description"
                    defaultMessage="Best for organized documents with clear hierarchies."
                  />
                </p>
              </div>
            </div>
          </div>
        </div>

        <div
          onClick={() => handleModeSelect("USE_SNOWFLAKE")}
          className={`
            relative flex cursor-pointer rounded-lg border p-3 shadow-sm focus:outline-none
            ${selectedMode === "USE_SNOWFLAKE" ? "border-sky-600 ring-1 ring-sky-600 bg-sky-50" : "border-slate-300 hover:bg-slate-50"}
            ${disabled ? "cursor-not-allowed" : ""}
          `}
          role="button"
          tabIndex={disabled ? -1 : 0}
          onKeyDown={(e) => {
            if ((e.key === "Enter" || e.key === " ") && !disabled) {
              handleModeSelect("USE_SNOWFLAKE");
            }
          }}
        >
          <div className="flex w-full items-center justify-between">
            <div className="flex items-center">
              <div className="text-sm">
                <div className="flex items-center gap-2 font-medium text-slate-900">
                  <Sparkles className="h-4 w-4 text-orange-500" />
                  <FormattedMessage
                    id="casesList.createCaseDialog.pipelineMode.useSnowflake.title"
                    defaultMessage="AI Processing Only"
                  />
                </div>
                <p className="text-slate-500 mt-1 text-xs">
                  <FormattedMessage
                    id="casesList.createCaseDialog.pipelineMode.useSnowflake.description"
                    defaultMessage="AI analyzes and organizes documents from scratch."
                  />
                </p>
              </div>
            </div>
          </div>
        </div>

        <div
          onClick={() => handleModeSelect("USE_SMART_AGENT")}
          className={`
            relative flex cursor-pointer rounded-lg border p-3 shadow-sm focus:outline-none
            ${selectedMode === "USE_SMART_AGENT" ? "border-sky-600 ring-1 ring-sky-600 bg-sky-50" : "border-slate-300 hover:bg-slate-50"}
            ${disabled ? "cursor-not-allowed" : ""}
          `}
          role="button"
          tabIndex={disabled ? -1 : 0}
          onKeyDown={(e) => {
            if ((e.key === "Enter" || e.key === " ") && !disabled) {
              handleModeSelect("USE_SMART_AGENT");
            }
          }}
        >
          <div className="flex w-full items-center justify-between">
            <div className="flex items-center">
              <div className="text-sm">
                <div className="flex items-center gap-2 font-medium text-slate-900">
                  <Bot className="h-4 w-4 text-sky-500" />
                  <FormattedMessage
                    id="casesList.createCaseDialog.pipelineMode.useSmartAgent.title"
                    defaultMessage="Smart AI Agent"
                  />
                  <span className="inline-block px-2 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-800 rounded">
                    <FormattedMessage
                      id="casesList.createCaseDialog.pipelineMode.useSmartAgent.recommended"
                      defaultMessage="Recommended"
                    />
                  </span>
                </div>
                <p className="text-slate-500 mt-1 text-xs">
                  <FormattedMessage
                    id="casesList.createCaseDialog.pipelineMode.useSmartAgent.description"
                    defaultMessage="AI Agent evaluates each document and chooses the best method."
                  />
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
